import { apiClient } from "../ApiClient.js";

export interface StructuredDraft<T = Record<string, unknown>> {
  id: string;
  kind: string;
  schema_version: number;
  version: number;
  content: T;
  content_hash: string;
  created_at?: string;
  updated_at?: string;
}

export interface StructuredDraftRevision<T = Record<string, unknown>> {
  version?: number;
  from_version?: number;
  to_version?: number;
  content_hash?: string;
  before_hash?: string;
  after_hash?: string;
  content?: T;
  created_at?: string;
  created_by?: string;
}

export interface StructuredDraftHistory<T = Record<string, unknown>> {
  items: StructuredDraftRevision<T>[];
}

export interface StructuredDraftArtifact {
  id: string;
  draft_id: string;
  draft_version: number;
  format: string;
  media_type?: string;
}

export interface StructuredDraftArtifactUrl {
  url: string;
  expires_in: number;
  media_type: string;
}

export function createStructuredDraft<T extends Record<string, unknown>>(input: {
  run_id?: string;
  kind: string;
  schema_version: number;
  content: T;
}): Promise<StructuredDraft<T>> {
  return apiClient.post<StructuredDraft<T>>("/structured-drafts", input);
}

export function getStructuredDraft<T extends Record<string, unknown>>(
  draftId: string,
): Promise<StructuredDraft<T>> {
  return apiClient.get<StructuredDraft<T>>(`/structured-drafts/${draftId}`);
}

export function patchStructuredDraft<T extends Record<string, unknown>>(
  draftId: string,
  version: number,
  operations: Array<{ op: "add" | "replace" | "remove"; path: string; value?: unknown }>,
): Promise<StructuredDraft<T>> {
  return apiClient.request<StructuredDraft<T>>(`/structured-drafts/${draftId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", "If-Match": String(version) },
    body: JSON.stringify({ operations }),
  });
}

export function getStructuredDraftHistory<T extends Record<string, unknown>>(
  draftId: string,
): Promise<StructuredDraftHistory<T>> {
  return apiClient.get<StructuredDraftHistory<T>>(`/structured-drafts/${draftId}/history`);
}

export function createStructuredDraftArtifact(
  draftId: string,
  format = "pdf",
): Promise<StructuredDraftArtifact> {
  return apiClient.post<StructuredDraftArtifact>(`/structured-drafts/${draftId}/artifacts`, {
    format,
    ttl_seconds: 300,
  });
}

export function getStructuredDraftArtifactUrl(
  artifactId: string,
): Promise<StructuredDraftArtifactUrl> {
  return apiClient.get<StructuredDraftArtifactUrl>(
    `/structured-drafts/artifacts/${artifactId}/url?expires_in=300`,
  );
}
